import React, { useState, useRef } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, endpoints } from "@/lib/api";
import { UploadCloud, X, CheckCircle2, ImageIcon } from "lucide-react";

const CATEGORIES = ["Infrastructure","Sanitation","Corruption","Environment","Health","Education","Consumer Rights","Women Safety","Child Welfare","Water","Electricity","Other"];

export const IssueReportForm = () => {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", location: "", category: "Infrastructure", description: "" });
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const onFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please upload an image (JPG/PNG)."); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image too large. Max 5MB."); return; }
    const reader = new FileReader();
    reader.onload = () => setPhoto({ name: file.name, size: file.size, dataUrl: reader.result });
    reader.readAsDataURL(file);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, photo_base64: photo?.dataUrl || null };
      await api.post(endpoints.issues, payload);
      setDone(true);
      toast.success("Thank you for reporting. We’ll acknowledge within 48 hours.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your inputs.");
    } finally { setLoading(false); }
  };

  if (done) return (
    <div data-testid="issue-form-success" className="rounded-xl border border-[hsl(var(--green)/0.25)] bg-[hsl(var(--surface-tint-green))] p-8 text-center">
      <CheckCircle2 className="h-10 w-10 text-[hsl(var(--green))] mx-auto" />
      <h3 className="heading-3 mt-3">Report received.</h3>
      <p className="lead mt-2">Thank you for being a responsible citizen. A CAF coordinator will review your report and share next steps within 48 hours.</p>
    </div>
  );

  return (
    <form onSubmit={onSubmit} className="grid gap-4 lg:grid-cols-2" data-testid="issue-form">
      <Field label="Full name*"><Input required data-testid="issue-form-full_name" value={form.full_name} onChange={set("full_name")} /></Field>
      <Field label="Phone*"><Input required data-testid="issue-form-phone" value={form.phone} onChange={set("phone")} /></Field>
      <Field label="Email*"><Input required type="email" data-testid="issue-form-email" value={form.email} onChange={set("email")} /></Field>
      <Field label="Location*"><Input required data-testid="issue-form-location" value={form.location} onChange={set("location")} placeholder="Address / landmark / city" /></Field>
      <Field label="Issue category*">
        <Select value={form.category} onValueChange={set("category")}>
          <SelectTrigger data-testid="issue-form-category"><SelectValue /></SelectTrigger>
          <SelectContent>{CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}</SelectContent>
        </Select>
      </Field>
      <div className="lg:col-span-2">
        <Field label="Describe the issue*"><Textarea required data-testid="issue-form-description" rows={4} value={form.description} onChange={set("description")} placeholder="Be specific: what, where, how long, who is affected." /></Field>
      </div>
      <div className="lg:col-span-2">
        <Label className="text-sm font-medium text-[hsl(var(--navy))]">Photo (optional)</Label>
        <div
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
          data-testid="issue-form-photo-dropzone"
          className="mt-2 cursor-pointer rounded-xl border-2 border-dashed border-[hsl(var(--border))] hover:border-[hsl(var(--navy))] p-6 flex items-center gap-4"
        >
          {photo ? (
            <div className="flex items-center gap-4 w-full">
              <img src={photo.dataUrl} alt={photo.name} className="h-20 w-20 rounded-md object-cover" />
              <div className="text-sm">
                <div className="font-medium text-[hsl(var(--navy))]">{photo.name}</div>
                <div className="text-xs text-[hsl(var(--muted-ink))]">{(photo.size/1024).toFixed(1)} KB</div>
              </div>
              <button type="button" onClick={(e) => { e.stopPropagation(); setPhoto(null); }} data-testid="issue-form-photo-remove" className="ml-auto h-8 w-8 rounded-md border border-[hsl(var(--border))] grid place-items-center"><X className="h-4 w-4" /></button>
            </div>
          ) : (
            <div className="flex items-center gap-3 text-sm text-[hsl(var(--muted-ink))]">
              <div className="h-10 w-10 rounded-md bg-[hsl(var(--surface-tint))] grid place-items-center text-[hsl(var(--navy))]"><UploadCloud className="h-5 w-5" /></div>
              <div>
                <div className="text-[hsl(var(--navy))] font-medium">Click to upload or drag & drop</div>
                <div className="text-xs">JPG or PNG, max 5MB</div>
              </div>
              <ImageIcon className="ml-auto h-5 w-5 text-[hsl(var(--muted-ink))]" />
            </div>
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="issue-form-photo-input" onChange={(e) => onFile(e.target.files?.[0])} />
        </div>
      </div>
      <div className="lg:col-span-2 flex items-center justify-between gap-3">
        <p className="text-xs text-[hsl(var(--muted-ink))]">Reports are reviewed and acted upon in coordination with local authorities. Please avoid sharing sensitive personal information.</p>
        <Button disabled={loading} type="submit" data-testid="issue-form-submit" className="bg-[hsl(var(--navy))] hover:bg-[hsl(var(--navy-2))] text-background">{loading ? "Submitting..." : "Submit report"}</Button>
      </div>
    </form>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm font-medium text-[hsl(var(--navy))]">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
